import { useQuery } from "@tanstack/vue-query";
import type { ReadonlyRefOrGetter } from "@vueuse/core";
import {
  type Price,
  type ProductDetails,
  type ProductsApiV2ProductsProductIdentifierCostEstimateGetRequest,
  SalesMethod,
} from "mws-ts-v2-sdk";

import { queries } from "~/queries";

// types to standardize the return of the composable between the two sales methods
// feel free to extend the types if you need more data

export type CostEstimations = {
  shippingOfficeName: string | null;
  shippingCost: Price | null;
  shippingOfficeCountryCode: string | null;
};

type ProvidedFilters = {
  destinationCountryCode: string | undefined;
};

/**
 * Wrapper to get the estimated costs for a product based on the sales method
 */
export default function useEstimatedProductCosts(
  product: ReadonlyRefOrGetter<ProductDetails>,
) {
  const { isShippingLocationConfirmed } = useShippingLocation();

  const {
    data: bidCosts,
    status: bidCostsStatus,
    providedFilters: providedBidCostsFilters,
  } = useBidCosts({
    product,
  });

  const {
    data: fixedPriceCosts,
    status: fixedPriceStatus,
    providedFilters: providedFixedPriceCostsFilters,
  } = useEstimatedFixedPriceProductCosts(product);

  const isBiddingProduct = computed(
    () => toValue(product).salesMethod === SalesMethod.Bidding,
  );

  const data = computed<CostEstimations | undefined>(() => {
    if (isBiddingProduct.value) {
      if (!bidCosts.value) return undefined;

      return {
        shippingOfficeName: bidCosts.value.shippingOfficeName,
        shippingOfficeCountryCode: bidCosts.value.shippingOfficeCountryCode,
        shippingCost: bidCosts.value.estimatedShippingCost,
      };
    }

    if (
      !fixedPriceCosts.value ||
      !providedFixedPriceCostsFilters.value.currency
    ) {
      return undefined;
    }

    return {
      shippingOfficeName: null,
      shippingOfficeCountryCode:
        bidCosts.value?.shippingOfficeCountryCode ?? null,
      shippingCost: {
        price: fixedPriceCosts.value.shippingCosts,
        currency: providedFixedPriceCostsFilters.value.currency,
      },
    };
  });

  const isLoading = computed(() => {
    if (isBiddingProduct.value) {
      return (
        isShippingLocationConfirmed.value && bidCostsStatus.value === "pending"
      );
    }

    return fixedPriceStatus.value === "pending";
  });

  const providedFilters = computed<ProvidedFilters>(() => {
    if (isBiddingProduct.value) {
      return {
        destinationCountryCode:
          providedBidCostsFilters.value.destinationCountryCode,
      };
    }

    return {
      destinationCountryCode: providedFixedPriceCostsFilters.value.countryCode,
    };
  });

  return {
    data,
    isLoading,
    providedFilters,
  };
}

/**
 * Gets the estimated costs for a fixed price product.
 * Is only enabled if the passed product has a fixed price sales method.
 * Will use the estimated user country in case they don't have an address in their account.
 */
function useEstimatedFixedPriceProductCosts(
  product: ReadonlyRefOrGetter<ProductDetails>,
) {
  const { activeCurrency } = useActiveCurrency();

  const { estimatedUserAddress } = useEstimatedUserShippingAddress();

  const request =
    computed<ProductsApiV2ProductsProductIdentifierCostEstimateGetRequest>(
      () => ({
        productIdentifier: toValue(product).slug,
        countryCode: estimatedUserAddress.value?.countryCode,
        stateCode: estimatedUserAddress.value?.state,
        currency: activeCurrency.value.isoCurrencyCode,
      }),
    );

  const isEstimatedCostQueryEnabled = computed(
    () =>
      toValue(product).salesMethod === SalesMethod.FixedPrice &&
      Boolean(request.value.countryCode),
  );

  const estimatedFixedPriceCostsQueryResult = useQuery({
    ...queries.products.estimatedCost(request),
    enabled: isEstimatedCostQueryEnabled,
    placeholderData: (data) => data,
  });

  return {
    ...estimatedFixedPriceCostsQueryResult,
    providedFilters: request,
  };
}
